import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";

import {
  SelectedDates,
  TimeSelect,
  StyledInput,
  StyledPaymentButton,
  TicketStyle,
  OptionLabel,
  OptionInput,
  OptionWrapper,
  ButtonPayment,
  CalendarTitle,
} from '../styles';

import { validateCoupon, getUser, createUserBooking } from '../api';
import { AddGuestComponent, InstantSuccessModal, PaymentModal } from '.';

const SelectedDatesComponent = ({ bookings, selectedDate }) => {
  const navigate = useNavigate();

  const [paymentData, setPaymentData] = useState(null);
  const [selectedOption, setSelectedOption] = useState('avulsa');
  const [modalOpen, setModalOpen] = useState(false);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [startTime, setStartTime] = useState('');
  const [endTimeOptions, setEndTimeOptions] = useState([]);
  const [selectedEndTime, setSelectedEndTime] = useState('');
  const [validatedCouponCode, setValidatedCouponCode] = useState(null);
  const [couponType, setCouponType] = useState(null); 
  const [instantSuccess, setInstantSuccess] = useState(false);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [couponCode, setCouponCode] = useState('');  // Para o código do cupom
  const [couponMessage, setCouponMessage] = useState('');
  const [isCouponValid, setIsCouponValid] = useState(false);
  const [discount, setDiscount] = useState(null);  // Estado para o desconto
  const [expiresAt, setExpiresAt] = useState(null);  // Estado para a data de expiração
  const [errorMessage, setErrorMessage] = useState("");

  const handleUsernamesChange = (usernames) => {
    setInvitedUsers(usernames); // Atualiza o estado com os usernames selecionados
  };

  // Função para formatar a data no formato dd-mm-aaaa
  const formatDate = (date) => {
    const [year, month, day] = date.split('-');
    return `${day}-${month}-${year}`;
  };

  // Formatação da data selecionada
  const displayText = selectedDate ? formatDate(selectedDate) : 'Nenhuma data escolhida';

  const handleValidateCoupon = async () => {
    try {
      const response = await validateCoupon(couponCode);

      // Se a resposta for ok (status 200)
      if (response.ok) {
        const data = await response.json();
        setCouponMessage(data.message);
        setIsCouponValid(data.isValid);

        // Atualizando os estados para desconto e data de expiração
        if (data.isValid) {
          setDiscount(data.discount);
          setExpiresAt(formatDate(data.expiresAt));
          setValidatedCouponCode(couponCode); // Armazenando o cupom validado
          setCouponType(data.type); // Atualiza o estado com o tipo do cupom

        } else {
          setDiscount(null);
          setExpiresAt(null);
          setValidatedCouponCode(null); // Se o cupom for inválido, removemos o cupom validado
          setCouponType(null); // Limpa o tipo de cupom quando inválido
        }

      } else if (response.status === 404) {
        // Se o status for 404, exibe a mensagem de erro personalizada
        setCouponMessage('Cupom expirado ou inválido. Não foi possível validar.');
        setIsCouponValid(false);
        setDiscount(null);
        setExpiresAt(null);
        setValidatedCouponCode(null); // Limpa o cupom validado
        setCouponType(null); // Limpa o tipo de cupom

      } else {
        // Em outros erros, exibe uma mensagem genérica
        setCouponMessage('Erro ao validar cupom');
        setIsCouponValid(false);
        setDiscount(null);
        setExpiresAt(null);
        setValidatedCouponCode(null); // Em caso de erro, removemos o cupom validado
        setCouponType(null); // Limpa o tipo de cupom
      }

    } catch (error) {
      setCouponMessage('Erro ao validar cupom');
      setIsCouponValid(false);
      setDiscount(null);
      setExpiresAt(null);
      setValidatedCouponCode(null); // Em caso de erro, removemos o cupom validado
      setCouponType(null); // Limpa o tipo de cupom
    }
  };

  const generateTimeSlots = (startTime, endTime) => {
    const startHour = parseInt(startTime.split(':')[0]);
    const startMinute = parseInt(startTime.split(':')[1]);
    const endHour = parseInt(endTime.split(':')[0]);
    const endMinute = parseInt(endTime.split(':')[1]);

    const timeSlots = [];
    let currentHour = startHour;
    let currentMinute = startMinute;

    // Função auxiliar para calcular a diferença em horas e minutos
    const calculateTimeDifference = (startHour, startMinute, endHour, endMinute) => {
      const startInMinutes = startHour * 60 + startMinute;
      const endInMinutes = endHour * 60 + endMinute;
      return (endInMinutes - startInMinutes) / 60; // Retorna a diferença em horas
    };

    // Primeiro horário sempre será o startTime se a diferença for >= 1 hora
    if (calculateTimeDifference(currentHour, currentMinute, endHour, endMinute) >= 1) {
      timeSlots.push(`${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`);
    }

    // Incremento de 30 em 30 minutos para os horários subsequentes
    while (calculateTimeDifference(currentHour, currentMinute, endHour, endMinute) > 1) {
      currentMinute += 30;
      if (currentMinute >= 60) {
        currentMinute = 0;
        currentHour++;
      }

      if (calculateTimeDifference(currentHour, currentMinute, endHour, endMinute) >= 1) {
        timeSlots.push(`${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`);
      } else {
        break;
      }
    }

    return timeSlots;
  };

  // Função para definir os horários disponíveis com base na data selecionada
  useEffect(() => {
    const updateAvailableTimes = () => {
      if (!selectedDate) return;
  
      const bookingForSelectedDate = bookings.find(booking => booking.date === selectedDate);
      if (bookingForSelectedDate && bookingForSelectedDate.available) {
        // Combine os horários de todas as instâncias disponíveis
        const allAvailableSlots = bookingForSelectedDate.available.flatMap(slot => {
          const { start_time, end_time } = slot;
          return generateTimeSlots(start_time, end_time); // Gera os horários de cada slot
        });
  
        setAvailableTimes(allAvailableSlots); // Atualiza o estado com todos os horários combinados
      } else {
        setAvailableTimes([]);
      }
    };

    updateAvailableTimes();
    setStartTime('');
    setSelectedEndTime('');

  }, [bookings, selectedDate]);

  const handleStartTimeChange = (event) => {
    setStartTime(event.target.value);
    generateEndTimeOptions(event.target.value);
    setSelectedEndTime('');
  };

  const generateEndTimeOptions = (start) => {
    if (!start) {
      setEndTimeOptions([]);
      return;
    }

    const startHour = parseInt(start.split(':')[0]);
    const startMinute = parseInt(start.split(':')[1]);
    const bookingForSelectedDate = bookings.find(booking => booking.date === selectedDate);
    if (!bookingForSelectedDate) return;

    const availableSlot = bookingForSelectedDate.available.find(slot => {
      const slotStartHour = parseInt(slot.start_time.split(':')[0]);
      const slotEndHour = parseInt(slot.end_time.split(':')[0]);
      return startHour >= slotStartHour && startHour < slotEndHour;
    });

    if (!availableSlot) return;

    const endTimeSlot = availableSlot.end_time;
    const [endHour, endMinute] = endTimeSlot.split(':').map(Number);

    const initialEndHour = startHour + 1; 
    const endTimeList = [];

    for (let hour = initialEndHour; hour <= endHour; hour++) {
      for (let minute = (hour === initialEndHour ? startMinute : 0); minute < 60; minute += 30) {
        if (hour === endHour && minute > endMinute) break;
        endTimeList.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
      }
    }

    setEndTimeOptions(endTimeList);
  };

  const handleEndTimeChange = (event) => {
    setSelectedEndTime(event.target.value);
  };

  const handlePayment = async () => {
    try {
      const type = (selectedOption === 'avulsa') ? 'unique' : 'fixed';

      const bodyData = {
        date: selectedDate,
        startTime,
        endTime: selectedEndTime,
        type,
        invitedUsers,
        couponCode: validatedCouponCode,
      };

      const resp = await getUser();
      
      if (!resp.ok) {
        localStorage.removeItem('token');
        navigate("/login", {replace: true});
        return;
      }

      const response = await createUserBooking(bodyData);

      if (!response.ok) {
        const errorData = await response.json();

        // Verifica se o erro é a mensagem "A marcação fixa não é permitida em fins de semana / feriados."
        if (errorData.error === "A marcação fixa não é permitida em fins de semana / feriados.") {
          setErrorMessage('A marcação fixa não é permitida em fins de semana / feriados.');
        } else if (response.status === 500) {
          setErrorMessage('Não foi possível realizar o agendamento. Por favor, selecione uma data e horários válidos.');
        } else if (response.status === 400) {
          setErrorMessage(`Erro: ${errorData.error}`);
        } else if (errorData.error && errorData.error.includes("Usuários convidados não foram encontrados")) {
          setErrorMessage('Um ou mais Usuários convidados não foram encontrados.');
        } else {
          setErrorMessage(`Erro ${response.status}: ${errorData.error || 'Erro desconhecido'}`);
        }

        throw new Error(`Erro ${response.status}: ${errorData.message || 'Erro desconhecido'}`);
      }

      const data = await response.json();

      if (data.instantConfirmation) {
        // Exibe o modal de sucesso imediato
        setInstantSuccess(true);

      } else {
        // Continua o fluxo normal com o modal de pagamento
        setPaymentData(data);
        setModalOpen(true);
      }

      setErrorMessage(""); // Limpar a mensagem de erro em caso de sucesso

    } catch (error) {
      console.error('Erro ao criar agendamento:', error);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        console.log('Código QR copiado para a área de transferência!');
      })
      .catch(err => {
        console.error('Erro ao copiar o código QR:', err);
      });
  };

  const handleCheckboxChange = (event) => {
    const { value, checked } = event.target;
    setSelectedOption(value); // Atualiza a opção selecionada

    if (checked) {
      // Se a checkbox foi marcada, desmarcar a outra
      const otherOption = value === 'avulsa' ? 'mensal' : 'avulsa';
      const otherCheckbox = document.querySelector(`input[value="${otherOption}"]`);
      if (otherCheckbox) {
        otherCheckbox.checked = false; // Desmarcar a outra opção
      }
    }
  };

  return (
    <div>
      <SelectedDates>
        <CalendarTitle>Realizar novo Agendamento</CalendarTitle>
        <OptionWrapper>
          <OptionLabel>
            <OptionInput
              type="checkbox"
              value="avulsa"
              checked={selectedOption === 'avulsa'}
              onChange={handleCheckboxChange} // Seleção de "Aula Avulsa"
            />
            Reserva Avulsa
          </OptionLabel>
          <OptionLabel>
            <OptionInput
              type="checkbox"
              value="mensal"
              checked={selectedOption === 'mensal'}
              onChange={handleCheckboxChange} // Seleção de "Aula Mensal"
            />
            Reserva Mensal
          </OptionLabel>
        </OptionWrapper>
        
        <span>
          <div style={{fontFamily: "Bellota", fontWeight: "600", color: "#1B76BB"}}>Data selecionada: </div>
        </span>
        <span style={{ color: 'black' , fontSize: "16px"}}>
          {displayText}
        </span>

        <div style={{display: "flex", alignItems: "flex-start", gap: "5px", flexDirection: "column"}}>
          <div style={{fontFamily: "Bellota", fontWeight: "600", color: "#1B76BB"}}>Início da Reserva:</div>
          <TimeSelect value={startTime} onChange={handleStartTimeChange}>
            <option value="">Escolha um horário</option>
            {availableTimes.map((time, index) => (
              <option key={index} value={time}>{time}</option>
            ))}
          </TimeSelect>
        </div>

        <div style={{display: "flex", alignItems: "flex-start", gap: "5px", flexDirection: "column"}}>
          <div style={{fontFamily: "Bellota", fontWeight: "600", color: "#1B76BB"}}>Fim da Reserva:</div>
          <TimeSelect value={selectedEndTime} onChange={handleEndTimeChange}>
            <option value="">Escolha um horário</option>
            {endTimeOptions.map((time, index) => (
              <option key={index} value={time}>{time}</option>
            ))}
          </TimeSelect>
        </div>

        <div>
          <div style={{fontFamily: "Bellota", fontWeight: "600", color: "#1B76BB"}}>Horário Selecionado:</div>
          <div style={{fontSize: "16px"}}>Início: {startTime}</div>
          <div style={{fontSize: "16px"}}>Fim: {selectedEndTime}</div>
        </div>
    
        <TicketStyle style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <AddGuestComponent invitedUsers={invitedUsers} onUsernamesChange={handleUsernamesChange} /> 
        </TicketStyle>

        <TicketStyle style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <div style={{
            margin: "0",
            fontFamily: "Bellota",
            height: '17px',
            fontWeight: "bold",
            color: "#1B76BB",
            textAlign: 'left' 
          }}>
            Cupom:
          </div>
          <StyledInput 
            placeholder="Digite seu cupom"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
            style={{ width: '100%', textAlign: 'left' }}
          />
          <StyledPaymentButton
            onClick={handleValidateCoupon}
            style={{ alignSelf: 'flex-start' }}
          >
            Validar
          </StyledPaymentButton>
          {couponMessage && !isCouponValid && (
            <div style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>
              <strong>{couponMessage}</strong>
            </div>
          )}

          {isCouponValid && (
            <div style={{ fontSize: '13px' }}>
              <div style={{ color: "green", fontWeight: "bold" }}>{couponMessage}</div>
              <div>
                <strong>Benefício do cupom:</strong>
                <div>
                  {couponType === 'percentage' 
                    ? `${parseFloat(discount).toFixed(0)}%` // Exibe o valor inteiro do desconto seguido de %
                    : `R$ ${parseFloat(discount).toFixed(2)}`}  {/* Exibe o valor fixo com 2 casas decimais */}
                </div>
              </div>
              <div>
                <strong>Data de expiração:</strong>
                <div>{expiresAt ? expiresAt : 'N/A'}</div>
              </div>
            </div>
          )}
          </TicketStyle>

        <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: "center" }}>
          <ButtonPayment onClick={handlePayment}>Agendar</ButtonPayment>
          {errorMessage && (
          <div style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>
            <strong>{errorMessage}</strong>
          </div>
        )}
        </div>
      </SelectedDates>
      {instantSuccess && (
        <InstantSuccessModal isOpen={instantSuccess} onClose={() => setInstantSuccess(false)} />
      )}

      {modalOpen && (
        <PaymentModal
          modalOpen={modalOpen}
          closeModal={closeModal}
          paymentData={paymentData}
          copyToClipboard={copyToClipboard}
          initialCountdown={300} // Temporizador inicial em segundos, exemplo: 300 para 5 minutos
        />
      )}
    </div>
  );
}

export default SelectedDatesComponent;
